'use client';

import { Fragment, ReactElement, useContext, useState } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { getFacetCode } from '@lamesarv-sdk/tools';

import { PropsWithClasses } from '@lamesarv-sdk/types';

import { CommonFormContext } from '.';

interface ICommonSelectProps extends PropsWithClasses {
  name: string;
  options: { title: string; value: string }[];
  optionsCustom?: ReactElement;
  label?: string;
  hideLabel?: boolean;
  usePlaceholder?: boolean;
  isRequired?: boolean;
  classContainer?: string;
  classLabel?: string;
  classLabelWithValue?: string;
  classInputWithValue?: string;
}

export const CommonSelect = (props: ICommonSelectProps) => {
  const [value, setValue] = useState<string>();
  const { isDisabled } = useContext(CommonFormContext);
  const { trigger } = useFormContext();

  return (
    <div className={twMerge('flex flex-col gap-1.5 group', isDisabled && 'opacity-60', props.classContainer)}>
      {!props.hideLabel && (
        <span className={twMerge('text-sm text-black-500', props.classLabel, !!value && props.classLabelWithValue)}>
          {props.label}
          {props.isRequired && <span className="ml-1 text-red-700 leading-none">*</span>}
        </span>
      )}
      <Controller
        name={props.name}
        defaultValue=""
        render={({ field, formState: { errors } }) => {
          const error = get(errors, props.name) as FieldError | null;
          return (
            <Fragment>
              <input type="hidden" name={field.name} value={field.value} />
              <select
                className={twMerge(
                  'w-full border border-black-200',
                  props.classInput,
                  !!value ? 'text-black-500' : 'text-black-350',
                  !!value && props.classInputWithValue,
                )}
                value={field.value}
                disabled={isDisabled}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  setValue(e.target.value);
                }}
                onBlur={() => {
                  trigger(props.name);
                }}
              >
                {!props.usePlaceholder && <option value="" disabled style={{ display: 'none' }}></option>}
                <option value="" disabled>
                  {(props.label ?? 'Select a location') + (props.usePlaceholder && props.isRequired ? '*' : '')}
                </option>
                {props.optionsCustom}
                {props.options.map((option) => {
                  const key = getFacetCode(option.value);
                  return (
                    <option key={key} value={option.value}>
                      {option.title}
                    </option>
                  );
                })}
              </select>

              {error && <span className="text-left text-red-700 text-sm">{error.message}</span>}
            </Fragment>
          );
        }}
      />
    </div>
  );
};
